import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Minus, Plus, Trash2, ShoppingBag, Loader2, CheckCircle2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { imgUrl, fmtGram, lineHasParts } from "@/lib/format";
import GramSummary from "@/components/GramSummary";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function Cart() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const hideMilyem = user?.hide_milyem;
  const { items, setQty, remove, clear, summary, count } = useCart();
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const submitOrder = async () => {
    setSubmitting(true);
    try {
      const payload = { items: items.map((i) => ({ product_id: i.product.id, quantity: i.quantity, size: i.size || null })), note };
      const { data } = await api.post("/orders", payload);
      clear();
      setSuccess(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-md mx-auto text-center py-16" data-testid="order-success">
        <CheckCircle2 className="mx-auto text-emerald-400 mb-4" size={64} strokeWidth={1.2} />
        <h1 className="font-display text-3xl text-foreground">Siparişiniz ORSE GOLD'a iletilmiştir.</h1>
        <p className="text-muted-foreground text-sm mt-3">
          Sipariş No: <span className="font-mono text-gold">{success.order_number}</span>
        </p>
        <p className="text-muted-foreground text-sm mt-1">Tahmini Toplam Has Altın: <span className="text-gold font-medium">~{fmtGram(success.total_has)} gr</span></p>
        <p className="text-[12px] text-amber-400/90 mt-2 max-w-sm mx-auto">Siparişiniz üretime alınacaktır. Kesin has altın ve bakiyeniz, ürünler üretilip gerçek gramları girildikten sonra kesinleşir.</p>
        {success.est_delivery && <p className="text-[13px] text-gold mt-2">Tahmini teslim / üretim süresi: <b>{success.est_delivery}</b></p>}
        {success.over_limit && (
          <div data-testid="over-limit-warning" className="mt-4 mx-auto max-w-sm text-sm text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-md px-4 py-3">
            Bu sipariş has altın limitinizi aşıyor. Siparişiniz onay için ORSE GOLD'a iletildi; en kısa sürede sizinle iletişime geçilecektir.
          </div>
        )}
        <div className="flex flex-col sm:flex-row gap-3 mt-8">
          <button onClick={() => navigate(`/invoice/${success.id}`)} data-testid="go-invoice" className="flex-1 h-11 bg-gold text-obsidian font-semibold rounded-md hover:bg-gold-hover transition-colors">
            Proforma Fatura
          </button>
          <button onClick={() => navigate("/orders")} data-testid="go-orders" className="flex-1 h-11 border border-border rounded-md text-foreground hover:border-gold transition-colors">
            Siparişlerim
          </button>
        </div>
      </div>
    );
  }

  if (count === 0) {
    return (
      <div className="text-center py-24" data-testid="cart-empty">
        <ShoppingBag className="mx-auto text-muted-foreground mb-4" size={48} strokeWidth={1.2} />
        <p className="text-muted-foreground">Sepetiniz boş.</p>
        <button onClick={() => navigate("/home")} className="mt-6 px-5 h-11 bg-gold text-obsidian font-semibold rounded-md hover:bg-gold-hover transition-colors">
          Ürünlere Göz At
        </button>
      </div>
    );
  }

  return (
    <div>
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-gold mb-4 transition-colors">
        <ArrowLeft size={16} /> Geri
      </button>
      <h1 className="font-display text-3xl text-foreground mb-1">Sepetim</h1>
      <p className="text-sm text-muted-foreground mb-6">{count} ürün</p>

      <div className="space-y-3">
        {items.map((it) => {
          const { gram: lineGram, total: lineHas } = lineHasParts(it.product, it.quantity);
          return (
            <div key={it.uid || it.product.id} data-testid={`cart-item-${it.product.id}`} className="flex gap-3 bg-surface border border-border rounded-md p-3">
              <div className="w-20 h-24 rounded overflow-hidden bg-elevated shrink-0">
                {it.product.image_url && <img src={imgUrl(it.product.image_url)} alt={it.product.name} className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground line-clamp-1">{it.product.name}</p>
                    <p className="font-mono text-[11px] text-muted-foreground">{it.product.code}</p>
                  </div>
                  <button data-testid={`cart-remove-${it.product.id}`} onClick={() => remove(it.uid || it.product.id)} className="text-muted-foreground hover:text-red-400 transition-colors p-1">
                    <Trash2 size={16} />
                  </button>
                </div>
                <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground">
                  <span>{it.product.karat} Ayar</span>
                  <span className="text-border">•</span>
                  <span>~{fmtGram(it.product.gram)} gr/adet</span>{it.size ? <span className="text-gold ml-1">· Boy {it.size}</span> : null}
                  {!hideMilyem && (<><span className="text-border">•</span><span>İşçilik {it.product.milyem || 0} milyem</span></>)}
                </div>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center border border-border rounded-md">
                    <button data-testid={`cart-dec-${it.product.id}`} onClick={() => setQty(it.uid || it.product.id, it.quantity - 1)} className="p-1.5 hover:text-gold transition-colors"><Minus size={14} /></button>
                    <span className="w-8 text-center text-sm">{it.quantity}</span>
                    <button data-testid={`cart-inc-${it.product.id}`} onClick={() => setQty(it.uid || it.product.id, it.quantity + 1)} className="p-1.5 hover:text-gold transition-colors"><Plus size={14} /></button>
                  </div>
                  <div className="text-right">
                    <div className="text-gold font-semibold text-sm">~{fmtGram(lineGram)} gr</div>
                    <div className="text-[11px] text-muted-foreground">tahmini has {fmtGram(lineHas)} gr</div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6">
        <label className="text-xs text-muted-foreground mb-1.5 block">Sipariş Notu (opsiyonel)</label>
        <Textarea
          data-testid="order-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Siparişinizle ilgili not ekleyin..."
          className="bg-surface border-border text-foreground focus-visible:ring-gold"
        />
      </div>

      <div className="mt-6">
        <GramSummary testid="cart-summary" breakdown={summary.breakdown} totalPureHas={summary.totalPureHas} totalIscHas={summary.totalIscHas} totalHas={summary.totalHas} estimated={true} />
      </div>

      <button
        data-testid="submit-order-button"
        onClick={submitOrder}
        disabled={submitting}
        className="w-full h-12 mt-4 bg-gold text-obsidian font-semibold rounded-md hover:bg-gold-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {submitting ? <Loader2 className="animate-spin" size={18} /> : "Siparişi Gönder"}
      </button>
      <p className="text-[11px] text-muted-foreground text-center mt-3">
        Online ödeme alınmaz. Bu bir siparişe-üretim (soğuk satış) siparişidir: gramlar tahminidir. Ürünler üretilip gerçek gramları belirlenince kesin has altın hesaplanır ve bakiyenize o an işlenir.
      </p>
    </div>
  );
}
